/**
 * Simple optimization polling service that bypasses the complex API client
 * Uses basic fetch to avoid any potential issues with the API layer
 */

import type { OptimizationResult } from '../../types/store';

export interface OptimizationProgress {
  current: number;
  total: number;
  progress: number;
  completed: boolean;
  result?: OptimizationResult;
  error?: string;
}

export class OptimizationPoller {
  private intervalId: NodeJS.Timeout | null = null;
  private isPolling = false;
  private consecutiveErrors = 0;
  private maxConsecutiveErrors = 15; // Increased from 8 to allow even more retries
  private currentTimeout = 30000; // Start with 30 seconds instead of 15
  private maxTimeout = 120000; // Maximum timeout of 2 minutes
  private baseTimeout = 30000; // Base timeout to reset to
  private pollingInterval = 8000; // Start with 8 seconds between polls

  async pollOptimization(
    optimizationId: string, 
    onProgress: (progress: OptimizationProgress) => void,
    onComplete: (result: OptimizationResult) => void,
    onError: (error: string) => void
  ): Promise<void> {
    if (this.isPolling) {
      this.stopPolling();
    }

    this.isPolling = true;
    this.consecutiveErrors = 0;  // Reset error count for new polling session
    this.currentTimeout = this.baseTimeout; // Reset timeout for new session
    this.pollingInterval = 8000; // Reset polling interval

    const poll = async () => {
      if (!this.isPolling) return;

      try {
        // Use the standard progress endpoint
        const baseUrl = import.meta.env.VITE_API_URL || '/api';
        const url = `${baseUrl}/optimize/progress/${optimizationId}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          // Use adaptive timeout based on previous errors
          signal: AbortSignal.timeout(this.currentTimeout)
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        // Reset error count and timeout on successful response
        this.consecutiveErrors = 0;
        // Gradually reduce timeout back to base level on success
        this.currentTimeout = Math.max(this.baseTimeout, this.currentTimeout - 5000);
        // Also reset polling interval on success
        this.pollingInterval = Math.max(8000, this.pollingInterval - 1000);

        if (data.success && data.data) {
          const progress: OptimizationProgress = {
            current: data.data.current || 0,
            total: data.data.total || 1600,
            progress: data.data.progress || 0,
            completed: data.data.completed || false,
            result: data.data.result,
            error: data.data.error
          };

          onProgress(progress);

          if (progress.completed) {
            this.stopPolling();
            
            // Fetch full results from the original endpoint
            try {
              const fullUrl = `${baseUrl}/optimize/progress/${optimizationId}`;
              
              const fullResponse = await fetch(fullUrl, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                signal: AbortSignal.timeout(60000) // Longer timeout for full results (60 seconds)
              });
              
              if (fullResponse.ok) {
                const fullData = await fullResponse.json();
                if (fullData.data.result) {
                  onComplete(fullData.data.result);
                } else {
                  onError('Optimization completed but no result data found');
                }
              } else {
                if (progress.result) {
                  onComplete(progress.result);
                } else {
                  onError('Optimization completed but no result data available');
                }
              }
            } catch (error) {
              if (progress.result) {
                onComplete(progress.result);
              } else {
                onError('Optimization completed but failed to fetch result data');
              }
            }
            return;
          }

          if (progress.error) {
            this.stopPolling();
            onError(progress.error);
            return;
          }
        }

      } catch (error) {
        this.consecutiveErrors++;
        
        // Increase timeout for next attempt if it's a timeout error
        if (error instanceof Error && (error.name === 'TimeoutError' || error.message.includes('timeout'))) {
          this.currentTimeout = Math.min(this.maxTimeout, this.currentTimeout + 10000);
          // Also increase polling interval to reduce server load
          this.pollingInterval = Math.min(20000, this.pollingInterval + 2000);
        }
        
        
        // Only treat as fatal error after multiple consecutive failures
        if (this.consecutiveErrors >= this.maxConsecutiveErrors) {
          this.stopPolling();
          if (error instanceof Error) {
            onError(`Polling failed after ${this.maxConsecutiveErrors} attempts: ${error.message}`);
          } else {
            onError(`Polling failed after ${this.maxConsecutiveErrors} attempts: Unknown error`);
          }
        } else {
          // Continue polling on temporary errors
        }
      }

      // Schedule next poll if still polling (using setTimeout for dynamic intervals)
      if (this.isPolling) {
        this.intervalId = setTimeout(poll, this.pollingInterval) as any;
      }
    };

    // Initial poll
    await poll();

    // Note: Subsequent polls are scheduled by the poll function itself using setTimeout
  }

  stopPolling(): void {
    this.isPolling = false;
    if (this.intervalId) {
      clearTimeout(this.intervalId);
      this.intervalId = null;
    }
  }
}

// Singleton instance
export const optimizationPoller = new OptimizationPoller();
